import React, { useState } from 'react';
import { TerminologyConfigType } from 'context/types';
import { GatewayPermissions } from 'auth/constants/permissions';
import { ThirdPartyOverviewSideModuleProps, ThirdPartyOwner } from './types';
import { Container, DefaultText } from './ThirdPartyOverviewSideModule.styles';
import { Collapsible } from 'components/atoms';
import SideModule from 'components/molecules/side-module';
import { ThirdPartyEdit } from 'components/organisms/third-party-edit';
import Modal from 'components/molecules/modal';
import { useCountryHelper } from 'helpers/countriesHelper';
import { genderMapper } from 'helpers/formDataHelper';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import { ThirdPartyEditResponse } from 'types/responses/thirdParty/thirdPartyEditResponse';
import { ThirdPartyType } from 'types';
import dayjs from 'dayjs';
import { dateWithoutTimeOffset } from 'helpers/dateHelper';

const ThirdPartyOverviewSideModule: React.FC<ThirdPartyOverviewSideModuleProps> = ({
  thirdParty,
  thirdPartyDataConfig,
}) => {
  let overview: JSX.Element;
  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [thirdPartyDetails, setThirdPartyDetails] = useState(thirdParty);

  const onEditThirdParty = (updated: ThirdPartyEditResponse) => {
    setThirdPartyDetails(updated);
    setIsModalOpen(false);
  };

  if (thirdParty?.type === ThirdPartyType.Business) {
    overview = (
      <BusinessOverview
        thirdParty={thirdPartyDetails}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
    );
  } else if (thirdParty?.type === ThirdPartyType.Individual) {
    overview = (
      <IndividualOverview
        thirdParty={thirdPartyDetails}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
    );
  } else if (thirdParty?.type === ThirdPartyType.Unknown) {
    overview = (
      <UnknownOverview
        thirdParty={thirdPartyDetails}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
    );
  } else {
    overview = (
      <DefaultText>Unsupported {terminologyConfig?.thirdPartySingularNaming} Type</DefaultText>
    );
  }

  const sideModuleProps = {
    header: terminologyConfig?.thirdPartySingularNaming + ' overview',
    onEdit: hasPermission(GatewayPermissions.EditThirdParty)
      ? () => setIsModalOpen(true)
      : undefined,
  };
  return (
    <>
      <SideModule {...sideModuleProps}>
        <Container>{overview}</Container>
      </SideModule>
      {thirdParty && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={`Edit ${terminologyConfig?.thirdPartySingularNaming} details`}
          removePadding={true}
        >
          <ThirdPartyEdit
            thirdParty={thirdPartyDetails}
            onCancel={() => setIsModalOpen(false)}
            onSave={onEditThirdParty}
            inModal={true}
          />
        </Modal>
      )}
    </>
  );
};

const GeneralThirdPartyInformation: React.FC<ThirdPartyOverviewSideModuleProps> = ({
  thirdParty,
  thirdPartyDataConfig,
  terminologyConfig,
}) => {
  const { getName } = useCountryHelper();
  return (
    <>
      <DefaultText>{`Name: ${thirdParty?.name ? thirdParty.name : '-'}`}</DefaultText>
      <DefaultText>{`Name (alternative script): ${
        thirdParty?.alternativeName ? thirdParty.alternativeName : '-'
      }`}</DefaultText>
      <>
        <DefaultText>{`${terminologyConfig?.internalIdSingularNaming}: ${
          thirdParty?.internalId ? thirdParty.internalId : '-'
        }`}</DefaultText>
        <DefaultText>{`Next Review Date: ${
          thirdParty?.nextReviewDate ? dayjs(thirdParty.nextReviewDate).format('DD/MM/YYYY') : '-'
        }`}</DefaultText>
        <ThirdPartyOwners
          thirdPartyOwners={thirdParty?.thirdPartyOwners}
          terminologyConfig={terminologyConfig}
        />
      </>
      <Address
        addressLine={thirdParty?.addressLineOne}
        city={thirdParty?.city}
        state={thirdParty?.county}
        postCode={thirdParty?.zipCode}
      />
      <DefaultText>{` ${thirdPartyDataConfig?.primaryCountry?.customName}: ${
        thirdParty?.primaryCountry ? getName(thirdParty.primaryCountry) : '-'
      }`}</DefaultText>
    </>
  );
};

const BusinessOverview: React.FC<ThirdPartyOverviewSideModuleProps> = ({
  thirdParty,
  thirdPartyDataConfig,
  terminologyConfig,
}) => {
  return (
    <>
      <GeneralThirdPartyInformation
        thirdParty={thirdParty}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
      <DefaultText>{`DUNS: ${thirdParty?.duns ? String(thirdParty.duns) : '-'}`}</DefaultText>
      <DefaultText>{`Registration Number: ${
        thirdParty?.registrationNumber ? String(thirdParty.registrationNumber) : '-'
      }`}</DefaultText>
      <DefaultText>{`SWIFT / BIC: ${
        thirdParty?.swift ? String(thirdParty.swift) : '-'
      }`}</DefaultText>
      <Collapsible collapsedText="Read more" expandedText="Read less">
        <>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField1?.customName}: ${
            thirdParty?.referenceField1 ? thirdParty.referenceField1 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField2?.customName}: ${
            thirdParty?.referenceField2 ? thirdParty.referenceField2 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField3?.customName}: ${
            thirdParty?.referenceField3 ? thirdParty.referenceField3 : '-'
          }`}</DefaultText>
        </>
      </Collapsible>
    </>
  );
};

const IndividualOverview: React.FC<ThirdPartyOverviewSideModuleProps> = ({
  thirdParty,
  thirdPartyDataConfig,
  terminologyConfig,
}) => {
  const { getName } = useCountryHelper();
  return (
    <>
      <GeneralThirdPartyInformation
        thirdParty={thirdParty}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
      <DefaultText>{`Gender: ${
        thirdParty && thirdParty?.gender !== null ? genderMapper(thirdParty.gender) : '-'
      }`}</DefaultText>
      <DefaultText>{`Date of birth: ${
        thirdParty && thirdParty?.dateOfBirth
          ? dayjs(dateWithoutTimeOffset(thirdParty.dateOfBirth)).format('DD/MM/YYYY')
          : '-'
      }`}</DefaultText>
      <DefaultText>{`Nationality: ${
        thirdParty && thirdParty?.nationality ? getName(thirdParty.nationality) : '-'
      }`}</DefaultText>
      <Collapsible collapsedText="Read more" expandedText="Read less">
        <DefaultText>{`Passport Number: ${
          thirdParty && thirdParty?.passportNumber ? thirdParty.passportNumber : '-'
        }`}</DefaultText>
        <DefaultText>{`National ID: ${
          thirdParty && thirdParty?.nationalIDNumber ? thirdParty.nationalIDNumber : '-'
        }`}</DefaultText>
        <DefaultText>{`Other ID: ${
          thirdParty && thirdParty?.otherIDNumber ? thirdParty.otherIDNumber : '-'
        }`}</DefaultText>
        <>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField1?.customName}: ${
            thirdParty && thirdParty?.referenceField1 ? thirdParty.referenceField1 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField2?.customName}: ${
            thirdParty && thirdParty?.referenceField2 ? thirdParty.referenceField2 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField3?.customName}: ${
            thirdParty && thirdParty?.referenceField3 ? thirdParty.referenceField3 : '-'
          }`}</DefaultText>
        </>
      </Collapsible>
    </>
  );
};

const UnknownOverview: React.FC<ThirdPartyOverviewSideModuleProps> = ({
  thirdParty,
  thirdPartyDataConfig,
  terminologyConfig,
}) => {
  return (
    <>
      <GeneralThirdPartyInformation
        thirdParty={thirdParty}
        thirdPartyDataConfig={thirdPartyDataConfig}
        terminologyConfig={terminologyConfig}
      />
      <Collapsible collapsedText="Read more" expandedText="Read less">
        <>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField1?.customName}: ${
            thirdParty?.referenceField1 ? thirdParty.referenceField1 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField2?.customName}: ${
            thirdParty?.referenceField2 ? thirdParty.referenceField2 : '-'
          }`}</DefaultText>
          <DefaultText>{`${thirdPartyDataConfig?.referenceField3?.customName}: ${
            thirdParty?.referenceField3 ? thirdParty.referenceField3 : '-'
          }`}</DefaultText>
        </>
      </Collapsible>
    </>
  );
};

interface ThirdPartyOwnerProps {
  thirdPartyOwners: ThirdPartyOwner[];
  terminologyConfig: TerminologyConfigType;
}

const ThirdPartyOwners: React.FC<ThirdPartyOwnerProps> = ({
  thirdPartyOwners,
  terminologyConfig,
}) => {
  let result: JSX.Element;

  if (!thirdPartyOwners) {
    result = <DefaultText>{`${terminologyConfig?.internalOwnerPluralNaming}: -`}</DefaultText>;
  } else if (thirdPartyOwners?.length === 0) {
    result = <DefaultText>{`${terminologyConfig?.internalOwnerPluralNaming}: -`}</DefaultText>;
  } else {
    result = (
      <>
        <DefaultText>{`${terminologyConfig?.internalOwnerPluralNaming}:`}</DefaultText>
        {thirdPartyOwners.map((owner) => (
          <DefaultText
            key={owner.users.id}
          >{`${owner?.users?.firstName} ${owner?.users?.lastName}`}</DefaultText>
        ))}
      </>
    );
  }

  return result;
};

interface AddressProps {
  addressLine: string;
  city: string;
  state: string;
  postCode: string;
}

const Address: React.FC<AddressProps> = ({ addressLine, city, state, postCode }) => {
  const address = [
    addressLine ? addressLine : null,
    city ? city : null,
    state ? state : null,
    postCode ? postCode : null,
  ]
    .filter((x) => x !== null)
    .join(' ');

  return <DefaultText>{`Address: ${address || '-'}`}</DefaultText>;
};

export default ThirdPartyOverviewSideModule;
